import { getQueryKey } from '@trpc/react-query';
import Typography from '@mui/material/Typography';
import {
  ClassificationFlow,
  LpInvestorClassificationStage,
} from 'further-types/lp/investor-classification';
import useDownloadFiles from 'hooks/ui/useDownloadFiles';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import { CalendarTime } from 'further-ui/utils';
import trpc from 'lib/trpcClient';
import ReviewButton from '../ReviewButton';

const knowledgeStagesUS = [
  LpInvestorClassificationStage.USLegalEntityKnowledge,
  LpInvestorClassificationStage.USIndividualKnowledge,
];

const experienceStagesUS = [
  LpInvestorClassificationStage.USLegalEntityExperience,
  LpInvestorClassificationStage.USIndividualExperience,
];

const knowledgeStages = [
  LpInvestorClassificationStage.EULegalEntityKnowledge,
  LpInvestorClassificationStage.UKLegalEntityKnowledge,
  LpInvestorClassificationStage.EUIndividualKnowledge,
  LpInvestorClassificationStage.UKIndividualKnowledge,
  ...knowledgeStagesUS,
];

const experienceStages = [
  LpInvestorClassificationStage.EULegalEntityExperience,
  LpInvestorClassificationStage.UKLegalEntityExperience,
  LpInvestorClassificationStage.EUIndividualExperience,
  LpInvestorClassificationStage.UKIndividualExperience,
  ...experienceStagesUS,
];

const testStageLabels = {
  ...Object.fromEntries(knowledgeStages.map((stage) => [stage, 'Knowledge'])),
  ...Object.fromEntries(experienceStages.map((stage) => [stage, 'Experience'])),
};

type Props = {
  classificationResult: {
    investorClassificationType: {
      name: string;
      classificationFlow: ClassificationFlow;
      stages: Array<{
        stage: LpInvestorClassificationStage;
        requiresPassingTest: boolean;
        availableTestAttempts?: number;
        requiredResult?: number;
      }>;
    };
    vehicleApplicationId: string;
    vehicleId: string;
    expiredAt: CalendarTime | null;
    stages: Array<{
      stage: LpInvestorClassificationStage;
      testAttemptsMade?: number;
      testResult?: number;
      manuallyApproved?: boolean;
      answers: Array<{
        answerFiles: Array<{
          previewUrl: string;
          name: string;
        }>;
      }>;
      declaration?: {
        files: Array<{
          previewUrl: string;
          name: string;
        }>;
      };
    }>;
  } | null;
  onboardingSettings: {
    investorClassificationSettings: Array<{
      classificationFlow: ClassificationFlow;
      requiredTestResult: number | null;
    }>;
    requireInvestorExperienceTesting: boolean;
    requireInvestorKnowledgeTesting: boolean;
  };
};

const InvestorClassificationResult: React.FC<Props> = ({
  classificationResult,
  onboardingSettings,
}) => {
  const vehicleId = classificationResult?.vehicleId ?? '';
  const vehicleApplicationId = classificationResult?.vehicleApplicationId ?? '';
  const { downloadFiles, isPending } = useDownloadFiles();
  const answerFiles =
    classificationResult?.stages.flatMap((stage) => [
      ...stage.answers.flatMap((answer) => answer.answerFiles),
      ...(stage.declaration?.files ?? []),
    ]) ?? [];

  const handleDownloadFiles = () => {
    downloadFiles({
      files: answerFiles.map((file) => ({
        url: file.previewUrl,
        name: file.name,
      })),
      fileName: 'investor-classification-files.zip',
    });
  };

  const invalidations = [
    getQueryKey(
      trpc.investorClassification.getClassificationResult,
      { vehicleId, vehicleApplicationId },
      'query',
    ),
    getQueryKey(
      trpc.onboarding.investorDetails,
      { vehicleId, vehicleApplicationId },
      'query',
    ),
  ];

  const {
    mutate: toggleExperienceStageApproval,
    isPending: isTogglingExperienceApproval,
  } = trpc.investorClassification.toggleExperienceStageApproval.useMutation(
    useHandleTrpcMutation()({
      invalidations,
    }),
  );

  const {
    mutate: toggleKnowledgeStageApproval,
    isPending: isTogglingKnowledgeApproval,
  } = trpc.investorClassification.toggleKnowledgeStageApproval.useMutation(
    useHandleTrpcMutation()({
      invalidations,
    }),
  );

  const classificationFlow =
    classificationResult?.investorClassificationType.classificationFlow;
  const stagesWithTests =
    classificationResult?.investorClassificationType.stages
      .filter(({ stage }) => {
        if (experienceStagesUS.includes(stage)) {
          return onboardingSettings.requireInvestorExperienceTesting;
        }
        if (knowledgeStagesUS.includes(stage)) {
          return onboardingSettings.requireInvestorKnowledgeTesting;
        }
        return true;
      })
      .filter((stage) => stage.requiresPassingTest);

  const testsDetails = stagesWithTests?.map((stage) => {
    const result = classificationResult?.stages.find(
      (s) => s.stage === stage.stage,
    );

    if (!result?.testAttemptsMade) {
      return {
        stage: stage.stage,
        status: 'Not attempted',
        requiresReview: false,
        approved: false,
      };
    }

    let status = '';
    let requiresReview = false;
    const testResult = result.testResult ?? 0;

    if (knowledgeStages.includes(stage.stage)) {
      const requiredResult = stage.requiredResult ?? 0;
      const passed = testResult >= requiredResult;
      const isLastAttempt =
        result.testAttemptsMade === stage.availableTestAttempts;
      requiresReview = !passed && isLastAttempt;

      status = result.manuallyApproved
        ? `Manually approved (3rd attempt not passed)`
        : requiresReview
        ? `Requires review (3rd attempt not passed)`
        : passed
        ? `Passed (on attempt ${result.testAttemptsMade} of ${stage.availableTestAttempts})`
        : `Failed (attempt ${result.testAttemptsMade} of ${stage.availableTestAttempts})`;
    }

    if (experienceStages.includes(stage.stage)) {
      const requiredTestResult =
        onboardingSettings.investorClassificationSettings.find(
          (s) => s.classificationFlow === classificationFlow,
        )?.requiredTestResult ?? 0;

      requiresReview = testResult > requiredTestResult;
      status = result.manuallyApproved
        ? `Manually approved (${testResult} risk points)`
        : requiresReview
        ? `Requires review (${testResult} risk points)`
        : `Passed (${testResult} risk points)`;
    }

    return {
      stage: stage.stage,
      status,
      requiresReview,
      approved: result.manuallyApproved ?? false,
    };
  });

  const getToggleApprovalHandler = (stage: LpInvestorClassificationStage) => {
    if (knowledgeStages.includes(stage)) {
      return () => {
        toggleKnowledgeStageApproval({
          vehicleId,
          vehicleApplicationId,
        });
      };
    }

    if (experienceStages.includes(stage)) {
      return () => {
        toggleExperienceStageApproval({
          vehicleId,
          vehicleApplicationId,
        });
      };
    }

    return undefined;
  };

  return (
    <>
      <FormField label="Investor type" spacing="xxs">
        <Row centered spacing="xs">
          <Typography variant="body2" color="grey.600">
            {classificationResult?.investorClassificationType.name ?? '--'}
          </Typography>
          {!!answerFiles.length && (
            <ReviewButton
              variant="text"
              onClick={handleDownloadFiles}
              disabled={isPending}
            >
              Download file(s)
            </ReviewButton>
          )}
        </Row>
      </FormField>

      {testsDetails?.map((test) => (
        <FormField
          label={`${testStageLabels[test.stage]} testing`}
          spacing="xxs"
          key={test.stage}
        >
          <Row spacing="xs">
            <Typography variant="body2" color="grey.600">
              {test.status}
            </Typography>
            {!classificationResult?.expiredAt && test.requiresReview ? (
              <ReviewButton
                variant="text"
                color="primary"
                onClick={getToggleApprovalHandler(test.stage)}
                disabled={
                  isTogglingExperienceApproval || isTogglingKnowledgeApproval
                }
              >
                {test.approved ? 'Unapprove' : 'Approve'}
              </ReviewButton>
            ) : null}
          </Row>
        </FormField>
      ))}
    </>
  );
};

export default InvestorClassificationResult;
